/**
 * Sync school coparenting serveur ↔ local — Phase 4, incrément 4.10.5.
 *
 * **Dernière sub-entité coparenting de la sous-phase 4.10**. Étend
 * ADR 0009 (server-wins) au champ `data.school` des foyers
 * coparenting.
 *
 * Symétrique de journal-sync (4.10.1) et expenses-sync (4.10.3) :
 * entrées immuables, pas de PATCH côté API. Le sub-sync migre les
 * entrées pré-auth via POST et rapatrie l'autorité via GET.
 *
 * Idempotent, filtre `home.mode === 'coparenting'`, déclenché en
 * parallèle des autres sub-syncs par `homes-sync.ts`.
 */
import * as schoolApi from '../api/school.js';
import { ApiError } from '../api/client.js';
import {
  applySchoolSyncResult,
  getHomesSnapshot,
  isServerHomeId,
  type ServerSchoolEntryPayload,
} from './homes.js';
import type { CoparentingHomeData, SchoolEntry } from '@nestor/shared-types';

export interface SchoolSyncStatus {
  state: 'idle' | 'syncing' | 'ok' | 'error';
  lastAt: number | null;
  errorCode: string | null;
}

let status: SchoolSyncStatus = { state: 'idle', lastAt: null, errorCode: null };
let inFlight: Promise<void> | null = null;

export function getSchoolSyncStatus(): SchoolSyncStatus {
  return status;
}

/**
 * Synchronise les entrées scolaires pour tous les foyers UUID
 * coparenting. Idempotent.
 */
export async function syncSchoolForAllHomes(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = doSync().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doSync(): Promise<void> {
  status = { state: 'syncing', lastAt: status.lastAt, errorCode: null };
  const { homes } = getHomesSnapshot();

  let anyError = false;
  let lastErrorCode: string | null = null;

  for (const home of homes) {
    if (!isServerHomeId(home.id)) continue;
    if (home.mode !== 'coparenting') continue;

    const data = home.data as CoparentingHomeData;

    // 1. Migration POST des entries locales pré-auth.
    // Phase 3.3.3 : offline-first via SendResult (cf. 3.3.2).
    const idMap = new Map<string, string>();
    const localOnly = data.school.filter((e) => !isServerHomeId(e.id));
    for (const entry of localOnly) {
      try {
        const result = await schoolApi.createSchoolEntry(
          home.id,
          buildCreateInput(entry),
          entry.id,
        );
        if (result.kind === 'sent') {
          idMap.set(entry.id, result.data.entry.id);
        }
      } catch (err) {
        const code = errorCode(err);
        anyError = true;
        lastErrorCode = code;
        console.warn('[school-sync] migration POST échouée', {
          homeId: home.id,
          entryId: entry.id,
          error: code,
        });
      }
    }

    // 2. Fetch + 3. apply.
    try {
      const serverEntries = await schoolApi.listSchoolEntries(home.id);
      const payload: ServerSchoolEntryPayload[] = serverEntries.map((e) => ({
        id: e.id,
        type: e.type,
        subject: e.subject,
        teacher: e.teacher,
        comment: e.comment,
        value: e.value,
        date: e.date,
      }));
      applySchoolSyncResult(home.id, { idMap, serverEntries: payload });
    } catch (err) {
      if (idMap.size > 0) {
        applySchoolSyncResult(home.id, { idMap });
      }
      const code = errorCode(err);
      anyError = true;
      lastErrorCode = code;
      console.warn('[school-sync] fetch /school échoué', { homeId: home.id, error: code });
    }
  }

  status = anyError
    ? { state: 'error', lastAt: Date.now(), errorCode: lastErrorCode }
    : { state: 'ok', lastAt: Date.now(), errorCode: null };
}

function buildCreateInput(entry: SchoolEntry): schoolApi.CreateSchoolEntryInput {
  return {
    type: entry.type,
    date: entry.date,
    ...(entry.subject ? { subject: entry.subject } : {}),
    ...(entry.teacher ? { teacher: entry.teacher } : {}),
    ...(entry.comment ? { comment: entry.comment } : {}),
    ...(entry.value ? { value: entry.value } : {}),
  };
}

function errorCode(err: unknown): string {
  if (err instanceof ApiError) {
    return err.code ?? `http_${err.status.toString()}`;
  }
  if (err instanceof Error) return err.name;
  return 'unknown';
}

/** @internal pour tests — reset le state module-level. */
export function _resetSchoolSyncForTests(): void {
  status = { state: 'idle', lastAt: null, errorCode: null };
  inFlight = null;
}
